/* Ramah card personalizer: a picture of the customer's card preview.
   At Add to cart, ramah-personalizer.js asks for a picture of the preview and
   uploads it with the order as "Card preview", the same way as a signature
   photo, so you can open exactly what the customer saw.
   The picture is redrawn from the preview as it is laid out on the screen:
   every word in its own spot and lettering, plus the fold line, the signing
   boxes and the signature photo, with a caption strip underneath. */
(function () {
  "use strict";

  var LONG_SIDE = 1800;  // pixels along the card's long side
  var BAND = 124;        // caption strip under the card, in pixels
  var MAX_PHOTO = 1400;  // signature photos are shrunk to this before drawing

  function alpha(color) {
    if (!color || color === "transparent") return 0;
    var m = /rgba?\(([^)]*)\)/.exec(color);
    if (!m) return 1;
    var parts = m[1].split(/[\s,\/]+/).filter(Boolean);
    return parts.length > 3 ? parseFloat(parts[3]) : 1;
  }

  function shown(el) {
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
  }

  function opacityOf(el, card) {
    var o = 1;
    for (var e = el; e && e !== card.parentNode; e = e.parentElement) {
      var v = parseFloat(getComputedStyle(e).opacity);
      o *= isNaN(v) ? 1 : v;
    }
    return o;
  }

  function dash(ctx, style, lw) {
    if (style === "dashed") ctx.setLineDash([3 * lw, 3 * lw]);
    else if (style === "dotted") { ctx.setLineDash([0, 2 * lw]); ctx.lineCap = "round"; }
    else ctx.setLineDash([]);
  }

  function drawBorders(ctx, cs, x, y, w, h, s) {
    var sides = ["Top", "Right", "Bottom", "Left"].map(function (n) {
      return {
        n: n,
        w: parseFloat(cs["border" + n + "Width"]) || 0,
        st: cs["border" + n + "Style"],
        col: cs["border" + n + "Color"]
      };
    });
    var same = sides.every(function (d) { return d.w === sides[0].w && d.st === sides[0].st && d.col === sides[0].col; });
    if (same) {
      var d = sides[0];
      if (!d.w || d.st === "none" || d.st === "hidden" || !alpha(d.col)) return;
      var lw = d.w * s, ins = lw / 2, rad = (parseFloat(cs.borderTopLeftRadius) || 0) * s;
      ctx.save();
      ctx.strokeStyle = d.col; ctx.lineWidth = lw; dash(ctx, d.st, lw);
      ctx.beginPath();
      if (rad && ctx.roundRect) ctx.roundRect(x + ins, y + ins, w - lw, h - lw, Math.max(0, rad - ins));
      else ctx.rect(x + ins, y + ins, w - lw, h - lw);
      ctx.stroke();
      ctx.restore();
      return;
    }
    sides.forEach(function (d) {
      if (!d.w || d.st === "none" || d.st === "hidden" || !alpha(d.col)) return;
      var lw = d.w * s, ins = lw / 2, a, b;
      if (d.n === "Top") { a = [x, y + ins]; b = [x + w, y + ins]; }
      else if (d.n === "Bottom") { a = [x, y + h - ins]; b = [x + w, y + h - ins]; }
      else if (d.n === "Left") { a = [x + ins, y]; b = [x + ins, y + h]; }
      else { a = [x + w - ins, y]; b = [x + w - ins, y + h]; }
      ctx.save();
      ctx.strokeStyle = d.col; ctx.lineWidth = lw; dash(ctx, d.st, lw);
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      ctx.restore();
    });
  }

  // The signature photo, as it looks on screen: grey, a little more contrast.
  function inkPhoto(img) {
    var k = Math.min(1, MAX_PHOTO / Math.max(img.naturalWidth, img.naturalHeight));
    var c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * k));
    c.height = Math.max(1, Math.round(img.naturalHeight * k));
    var g = c.getContext("2d");
    g.drawImage(img, 0, 0, c.width, c.height);
    try {
      var data = g.getImageData(0, 0, c.width, c.height), p = data.data;
      for (var i = 0; i < p.length; i += 4) {
        var v = (0.2126 * p[i] + 0.7152 * p[i + 1] + 0.0722 * p[i + 2] - 128) * 1.35 + 128;
        v = v < 0 ? 0 : (v > 255 ? 255 : v);
        p[i] = p[i + 1] = p[i + 2] = v;
      }
      g.putImageData(data, 0, 0);
    } catch (e) { /* leave the photo as it is */ }
    return c;
  }

  function drawImage(ctx, img, cs, x, y, w, h, s) {
    if (!img.complete || !img.naturalWidth) return;
    var bl = (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.paddingLeft) || 0);
    var bt = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0);
    var br = (parseFloat(cs.borderRightWidth) || 0) + (parseFloat(cs.paddingRight) || 0);
    var bb = (parseFloat(cs.borderBottomWidth) || 0) + (parseFloat(cs.paddingBottom) || 0);
    var cx = x + bl * s, cy = y + bt * s, cw = w - (bl + br) * s, ch = h - (bt + bb) * s;
    var dx = cx, dy = cy, dw = cw, dh = ch;
    if (cs.objectFit === "contain") {
      var r = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
      dw = img.naturalWidth * r; dh = img.naturalHeight * r;
      dx = cx + (cw - dw) / 2; dy = cy + (ch - dh) / 2;
    }
    ctx.save();
    if (cs.mixBlendMode === "multiply") ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(cs.filter && cs.filter !== "none" ? inkPhoto(img) : img, dx, dy, dw, dh);
    ctx.restore();
  }

  function fontOf(cs, s) {
    return cs.fontStyle + " " + cs.fontWeight + " " + (parseFloat(cs.fontSize) * s) + "px " + cs.fontFamily;
  }

  // Every piece of text in the card, one word at a time (one letter at a time
  // where letters are spaced out, or a long word breaks across lines).
  function textRuns(card) {
    var runs = [];
    var walker = document.createTreeWalker(card, NodeFilter.SHOW_TEXT, null);
    var range = document.createRange();
    var node;
    while ((node = walker.nextNode())) {
      var text = node.data;
      if (!/\S/.test(text)) continue;
      var host = node.parentElement;
      if (!host || !shown(host)) continue;
      var cs = getComputedStyle(host);
      var op = opacityOf(host, card);
      if (!op) continue;
      var spaced = (parseFloat(cs.letterSpacing) || 0) !== 0;
      var upper = cs.textTransform === "uppercase";
      var re = /\S+/g, w;
      while ((w = re.exec(text))) {
        range.setStart(node, w.index);
        range.setEnd(node, w.index + w[0].length);
        var rects = range.getClientRects();
        if (rects.length === 1 && !spaced) {
          runs.push({ t: w[0], r: rects[0], cs: cs, op: op, up: upper });
        } else {
          for (var i = 0; i < w[0].length; i++) {
            range.setStart(node, w.index + i);
            range.setEnd(node, w.index + i + 1);
            var rc = range.getClientRects()[0];
            if (rc) runs.push({ t: w[0].charAt(i), r: rc, cs: cs, op: op, up: upper });
          }
        }
      }
    }
    return runs;
  }

  function fitText(ctx, text, weight, size, maxW) {
    var f = size;
    do {
      ctx.font = weight + " " + f + "px Helvetica, Arial, sans-serif";
      if (ctx.measureText(text).width <= maxW) break;
      f -= 1;
    } while (f > 14);
  }

  function capture(card, lines) {
    return new Promise(function (resolve) {
      var r0 = card.getBoundingClientRect();
      if (!r0.width || !r0.height) { resolve(null); return; }
      var s = LONG_SIDE / Math.max(r0.width, r0.height);
      var W = Math.round(r0.width * s), H = Math.round(r0.height * s);
      var runs = textRuns(card);

      // Make sure every lettering used is ready before drawing with it.
      var fonts = {};
      runs.forEach(function (run) { fonts[fontOf(run.cs, s)] = true; });
      var loading = document.fonts && document.fonts.load
        ? Promise.all(Object.keys(fonts).map(function (f) { return document.fonts.load(f).catch(function () {}); }))
        : Promise.resolve();

      loading.then(function () {
        var canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H + (lines && lines.length ? BAND : 0);
        var ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        var cardCs = getComputedStyle(card);
        if (alpha(cardCs.backgroundColor)) {
          ctx.fillStyle = cardCs.backgroundColor;
          ctx.fillRect(0, 0, W, H);
        }

        // Boxes, lines and pictures first, then the words on top.
        [card].concat(Array.prototype.slice.call(card.querySelectorAll("*"))).forEach(function (el) {
          if (!shown(el)) return;
          var cs = getComputedStyle(el);
          var op = opacityOf(el, card);
          if (!op) return;
          var r = el.getBoundingClientRect();
          var x = (r.left - r0.left) * s, y = (r.top - r0.top) * s, w = r.width * s, h = r.height * s;
          ctx.globalAlpha = op;
          if (el !== card && alpha(cs.backgroundColor)) {
            ctx.fillStyle = cs.backgroundColor;
            ctx.fillRect(x, y, w, h);
          }
          drawBorders(ctx, cs, x, y, w, h, s);
          if (el.tagName === "IMG") drawImage(ctx, el, cs, x, y, w, h, s);
          ctx.globalAlpha = 1;
        });

        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        runs.forEach(function (run) {
          ctx.font = fontOf(run.cs, s);
          ctx.fillStyle = run.cs.color;
          ctx.globalAlpha = run.op;
          var m = ctx.measureText("Hg");
          var asc = m.fontBoundingBoxAscent, desc = m.fontBoundingBoxDescent;
          var size = parseFloat(run.cs.fontSize) * s;
          if (!(asc > 0) || !(desc >= 0)) { asc = size * 0.8; desc = size * 0.2; }
          var top = (run.r.top - r0.top) * s, h = run.r.height * s;
          var str = run.up ? run.t.toUpperCase() : run.t;
          var x = (run.r.left - r0.left) * s, y = top + (h - asc - desc) / 2 + asc;
          // Keep each word to its width on screen, so the gaps between words stay put.
          var tw = run.r.width * s;
          if (tw > 0) ctx.fillText(str, x, y, tw + 0.5);
          else ctx.fillText(str, x, y);
        });
        ctx.globalAlpha = 1;

        // Caption strip: what this is and when it was made.
        if (lines && lines.length) {
          ctx.fillStyle = "#f3f1ec";
          ctx.fillRect(0, H, W, BAND);
          ctx.fillStyle = "#d9d4c9";
          ctx.fillRect(0, H, W, 2);
          ctx.fillStyle = "#2f2c28";
          fitText(ctx, lines[0], "600", 32, W - 72);
          ctx.fillText(lines[0], 36, H + 52);
          if (lines[1]) {
            ctx.fillStyle = "#6b675f";
            fitText(ctx, lines[1], "400", 26, W - 72);
            ctx.fillText(lines[1], 36, H + 94);
          }
        }

        if (canvas.toBlob) canvas.toBlob(function (blob) { resolve(blob || null); }, "image/png");
        else resolve(null);
      }).catch(function () { resolve(null); });
    });
  }

  window.RamahPreviewCapture = {
    capture: function (card, lines) {
      try { return capture(card, lines); } catch (e) { return Promise.resolve(null); }
    }
  };
})();